import { FIREBASE_VAPID_KEY, FIREBASE_WEB_CONFIG, WEB_PUSH_CONFIGURED } from '@/config'
import { registerDevice, removeInstallation } from '@/services/api/device.service'
import { logger } from '@/utils/logger'
import { isNativeApp } from '@/utils/platform'

import { describeBrowser } from './browserInfo'
import { installationId } from './deviceIdentity'

/**
 * Browser notifications (Web Push through Firebase Cloud Messaging). The Firebase SDK is imported
 * on demand, so it only loads for people who turned notifications on. The browser asks for
 * permission only from {@link enableWebPush}, which is called from a click, never on page load.
 */

export type WebPushState =
  /** Not offered: no Firebase config in this build, or the Android app (which has its own push). */
  | 'unconfigured'
  | 'unsupported'
  /** The browser's permission is denied; only the browser's site settings can undo it. */
  | 'blocked'
  | 'off'
  | 'on'

const OPT_OUT_KEY = 'push.webOptOut'
const SERVICE_WORKER_URL = '/push-sw.js'

function read(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function write(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    // storage blocked: the setting just won't persist
  }
}

/** What the notifications control should show right now. */
export function currentWebPushState(): WebPushState {
  if (isNativeApp() || !WEB_PUSH_CONFIGURED) return 'unconfigured'
  if (typeof window === 'undefined' || !('serviceWorker' in navigator) || !('Notification' in window)
    || !('PushManager' in window)) {
    return 'unsupported'
  }
  if (Notification.permission === 'denied') return 'blocked'
  if (Notification.permission === 'granted' && read(OPT_OUT_KEY) !== 'true') return 'on'
  return 'off'
}

async function messagingInstance() {
  const [{ getApps, initializeApp }, { getMessaging, isSupported }] = await Promise.all([
    import('firebase/app'),
    import('firebase/messaging'),
  ])
  if (!(await isSupported())) return null
  const app = getApps()[0] ?? initializeApp(FIREBASE_WEB_CONFIG)
  return getMessaging(app)
}

/**
 * Saves this browser's push token for the current workspace. Safe to repeat: the server upserts by
 * installation id, so it runs on every app load and workspace switch.
 */
export async function registerWebPush(): Promise<void> {
  if (currentWebPushState() !== 'on') return
  const messaging = await messagingInstance()
  if (!messaging) return
  const { getToken } = await import('firebase/messaging')
  const serviceWorkerRegistration = await navigator.serviceWorker.register(SERVICE_WORKER_URL)
  const token = await getToken(messaging, { vapidKey: FIREBASE_VAPID_KEY, serviceWorkerRegistration })
  if (!token) return
  await registerDevice({
    installationId: await installationId(),
    platform: 'WEB',
    deviceLabel: describeBrowser(navigator.userAgent),
    fcmToken: token,
  })
}

/** Asks the browser for permission (call from a click) and registers on success. */
export async function enableWebPush(): Promise<WebPushState> {
  if (currentWebPushState() === 'unconfigured' || currentWebPushState() === 'unsupported') {
    return currentWebPushState()
  }
  write(OPT_OUT_KEY, null)
  if (Notification.permission === 'default') {
    await Notification.requestPermission()
  }
  if (currentWebPushState() === 'on') {
    try {
      await registerWebPush()
    } catch (error) {
      logger.warn('Could not register this browser for notifications', error)
    }
  }
  return currentWebPushState()
}

/** Stops notifications in this browser for every workspace: forgets it on the server and drops the push token. */
export async function disableWebPush(): Promise<WebPushState> {
  // Opt out first: even if the network calls fail, the app will not re-register on the next load.
  write(OPT_OUT_KEY, 'true')
  try {
    await removeInstallation(await installationId())
  } catch (error) {
    logger.warn('Could not remove this browser from the server', error)
  }
  await dropBrowserPushToken()
  return currentWebPushState()
}

/** Deletes this browser's push token, so FCM refuses it and the server forgets it on the next send. */
export async function dropBrowserPushToken(): Promise<void> {
  try {
    const messaging = await messagingInstance()
    if (messaging) {
      const { deleteToken } = await import('firebase/messaging')
      await deleteToken(messaging)
    }
  } catch (error) {
    logger.warn('Could not delete the browser push token', error)
  }
}
